import type { GetServerSideProps, NextPage } from 'next'
import { GlobalRequest } from '~/components/global-requset'
import { Container } from '~/components/ui/Container'
import { Layout } from '~/layouts/default'
import { PageCrumbs } from '~/components/business/PageCrumbs'
import styled from 'styled-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { GET_PRODUCT_LIST, GET_CATEGORY_LIST } from '~/graphqls/gqls'
import {
  getProductList,
  getProductList_itemModule_productList_edges_node,
  getCategoryList,
  ArtworkSortEnum,
  SortType,
  getProductListVariables,
  ArtworkDBType,
  CrCategoryFilter,
} from '~/graphqls/types'
import { getGraphData, uniqueArray } from '~/utils'
import { Col, message, Row } from 'antd'
import { SingleProduct } from '~/components/business/SingleProduct'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { useRouter } from 'next/router'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { InfiniteScroll } from '~/components/business/InfiniteScroll'
import { PAGE_SIZE } from '~/configs'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { SEOHead } from '~/components/business/SEOHead'
import { MyTab, CateDataProps } from '~/components/ui/MyTab'
import { PageSort, SortItemProps } from '~/components/business/PageSort'
import { PageTabs } from '~/components/business/PageTabs'

const StyledMarket = styled.div``

const Market: NextPage<{
  categoryList: CateDataProps[]
  productList: getProductList_itemModule_productList_edges_node[]
  initCateIndex: number
  initParamSubCateIds: number[] | null
  hasNextPage: boolean
  artworkDBType: ArtworkDBType
  workType: string
}> = ({
  categoryList,
  productList,
  initCateIndex,
  hasNextPage: nextPage,
  initParamSubCateIds,
  artworkDBType,
  workType,
}) => {
  const router = useRouter()
  const [loading, setLoading] = useState(false)

  const [cateIndex, setCateIndex] = useState(initCateIndex)
  // 选中的二级类目
  const [activeSubCateIds, setActiveSubCateIds] = useState<number[]>([])
  // 分类参数
  const [paramSubCateIds, setParamSubCateIds] = useState(initParamSubCateIds)

  const [sortType, setSortType] = useState(SortType.DESC)
  const [sortField, setSortFiled] = useState(ArtworkSortEnum.VERIFY_TIME)

  const [productData, setProductData] = useState(productList)
  const [hasNextPage, setHasNextPage] = useState(nextPage)

  const [pageInfo, setPageInfo] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  })
  const sortData: SortItemProps[] = useMemo(() => {
    const array: SortItemProps[] = [
      {
        key: ArtworkSortEnum.VERIFY_TIME,
        name: '最新',
      },
      {
        key: ArtworkSortEnum.HOT,
        name: '最热',
      },
    ]
    if (workType !== 'ai') {
      array.push({
        key: ArtworkSortEnum.PRICE,
        name: '价格',
        sort: true,
      })
    }
    return array
  }, [workType])

  useEffect(() => {
    // tab切换的时候重置参数
    setCateIndex(initCateIndex)
    setSortType(SortType.DESC)
    setSortFiled(ArtworkSortEnum.VERIFY_TIME)
    setParamSubCateIds(initParamSubCateIds)
    setActiveSubCateIds([])
    setProductData(productList)
    setHasNextPage(nextPage)
    setPageInfo({ page: 1, pageSize: PAGE_SIZE })
  }, [workType])

  const getArtAction = useImperativeQuery<
    getProductList,
    getProductListVariables
  >(GET_PRODUCT_LIST)

  const optCateData: CateDataProps[] = useMemo(() => {
    return [
      {
        id: 0,
        name: '全部',
        categoryList: [],
      },
      ...(categoryList || []).map(item => ({
        id: item.id,
        name: item.name,
        categoryList: item.categoryList,
      })),
    ].map((item, index) => {
      return {
        id: item.id,
        name: item.name,
        categoryList: [
          {
            id: -(index + 1),
            name: '全部',
          },
          ...(item.categoryList || []).map(subItem => ({
            id: subItem.id,
            name: subItem.name,
          })),
        ],
      }
    })
  }, [categoryList])

  const onCateItem = useCallback(
    (selectIndex: number) => {
      if (cateIndex === selectIndex) {
        return
      }

      setPageInfo({
        page: 1,
        pageSize: PAGE_SIZE,
      })

      setCateIndex(selectIndex)
      setActiveSubCateIds([])

      if (selectIndex === 0) {
        setParamSubCateIds(null)
        const query: any = {
          type: router.query.type,
        }
        if (router.query.keyword) {
          query.keyword = router.query.keyword
        }
        router.replace({
          pathname: router.pathname,
          query,
        })
      } else {
        if (workType === 'ai') {
          setParamSubCateIds([optCateData[selectIndex]?.id as number])
        } else {
          setParamSubCateIds(
            optCateData[selectIndex].categoryList
              ?.filter(item => item.id > 0)
              .map(item => item.id as number) || []
          )
        }

        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              id: optCateData[selectIndex].id,
            },
          },
          undefined,
          {
            shallow: true,
          }
        )
      }
    },
    [cateIndex, optCateData]
  )

  const onSubCateItem = useCallback(
    (selectId: number) => {
      if (!activeSubCateIds.length && selectId <= 0) return
      setPageInfo({
        page: 1,
        pageSize: PAGE_SIZE,
      })
      if (selectId <= 0) {
        setActiveSubCateIds([])
        setParamSubCateIds(
          optCateData[cateIndex].categoryList
            ?.filter(item => item.id > 0)
            .map(item => item.id as number) || []
        )
      } else if (activeSubCateIds.includes(selectId)) {
        setActiveSubCateIds(ids => {
          return ids.filter(id => id !== selectId)
        })
        setParamSubCateIds(ids => {
          const filterIds = ids?.filter(id => id !== selectId) || []
          if (!filterIds.length) {
            return (
              optCateData[cateIndex].categoryList
                ?.filter(item => item.id > 0)
                .map(item => item.id as number) || []
            )
          }
          return filterIds
        })
      } else {
        setActiveSubCateIds(ids => Array.from(new Set([selectId, ...ids])))
        setParamSubCateIds(ids => {
          // 备注:当之前被选中的是全部，就只保留新选中的
          if (
            Number(optCateData[cateIndex].categoryList?.length) -
              Number(ids?.length) ===
            1
          ) {
            return [selectId]
          }
          return Array.from(new Set([selectId, ...(ids || [])]))
        })
      }
    },
    [activeSubCateIds, optCateData, cateIndex]
  )

  const onSortChange = (item: SortItemProps, sortType: SortType) => {
    setPageInfo({
      page: 1,
      pageSize: PAGE_SIZE,
    })
    setSortFiled(item.key as ArtworkSortEnum)
    setSortType(sortType)
  }

  const onScroll = useCallback(() => {
    setLoading(true)

    getArtAction({
      offset: pageInfo.pageSize * pageInfo.page,
      first: pageInfo.pageSize,
      params: {
        categoryIdList: paramSubCateIds,
        sort: sortType,
        sortField: sortField,
        searchName: String(router.query.keyword || ''),
        type: artworkDBType,
      },
    })
      .then(data => {
        setPageInfo(page => {
          return {
            pageSize: page.pageSize,
            page: page.page + 1,
          }
        })
        setHasNextPage(
          data?.data?.itemModule?.productList?.pageInfo?.hasNextPage || false
        )
        setProductData(product => {
          return uniqueArray(
            product,
            getGraphData(data?.data?.itemModule?.productList?.edges || [])
          )
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [
    pageInfo,
    getArtAction,
    paramSubCateIds,
    sortType,
    sortField,
    router.query.keyword,
  ])

  useDidUpdateEffect(() => {
    getArtAction({
      offset: 0,
      first: PAGE_SIZE,
      params: {
        categoryIdList: paramSubCateIds,
        sort: sortType,
        sortField: sortField,
        searchName: String(router.query.keyword || ''),
        type: artworkDBType,
      },
    })
      .then(data => {
        setHasNextPage(
          data?.data?.itemModule?.productList?.pageInfo?.hasNextPage || false
        )
        setProductData(
          getGraphData(data?.data?.itemModule?.productList?.edges || [])
        )
      })
      .catch(err => {
        message.destroy()
        message.error('获取数据失败，请重试')
      })
  }, [paramSubCateIds, sortType, sortField, router.query.keyword])

  return (
    <Layout>
      <SEOHead
        title="拍卖市场"
        desc="海量作品库，丰富多彩；发现潜力作品，欣赏不同审美。"
      />
      <StyledMarket>
        <Container>
          {/* <PageCrumbs title="市场" /> */}
          <PageTabs
            value={workType}
            data={[
              { key: 'ai', name: 'AI 作品' },
              { key: 'human', name: '人类作品' },
            ]}
            onChange={({ key }) => {
              router.replace({
                pathname: router.pathname,
                query: {
                  type: key,
                },
              })
            }}
            isv2={true}
          />
          <MyTab
            menu={optCateData}
            active={cateIndex}
            activeSub={activeSubCateIds}
            onChange={onCateItem}
            onSubChange={onSubCateItem}
            hideSubMenu={workType === 'ai'}
          />
          <PageSort data={sortData} onChange={onSortChange} />
          {productData.length ? (
            <>
              <Row gutter={32}>
                {productData.map(product => (
                  <Col span={6} key={product.id}>
                    <SingleProduct data={product} showRank />
                  </Col>
                ))}
              </Row>
              <InfiniteScroll
                onScroll={onScroll}
                hasNextPage={hasNextPage}
                loading={loading}
              />
            </>
          ) : (
            <MyEmpty />
          )}
        </Container>
      </StyledMarket>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { initialState, client } = await GlobalRequest(context)

  // 备注：参数中传递的都是一级分类
  const cateId = context.query.id ? Number(context.query.id) : null
  const keyword = String(context.query.keyword || '')
  const workType = context.query.type || 'ai'

  const artworkDBType =
    workType === 'ai' ? ArtworkDBType.AI_COPYRIGHT : ArtworkDBType.NORMAL
  const categoryListRes = await client
    .query<getCategoryList>({
      query: GET_CATEGORY_LIST,
      fetchPolicy: 'no-cache',
      variables: {
        crCategoryFilter:
          workType === 'ai' ? CrCategoryFilter.AI_MARKET : CrCategoryFilter.ALL,
      },
    })
    .catch(() => {})

  const categoryList =
    categoryListRes?.data?.marketingModule?.categoryList || []

  const paramSubCateIds = () => {
    if (!cateId) return null
    if (workType === 'ai') {
      return [cateId]
    }
    const subCateList = categoryList.find(item => {
      return item?.id === cateId
    })?.categoryList

    return subCateList?.map(item => item?.id)
  }

  const productRes = await client.query<
    getProductList,
    getProductListVariables
  >({
    query: GET_PRODUCT_LIST,
    variables: {
      offset: 0,
      first: PAGE_SIZE,
      params: {
        categoryIdList: paramSubCateIds(),
        sortField: ArtworkSortEnum.VERIFY_TIME,
        type: artworkDBType,
        sort: SortType.DESC,
        searchName: keyword,
      },
    },
    fetchPolicy: 'no-cache',
  })

  const initCateIndex = () => {
    if (!cateId) return 0

    // 备注：加1是因为实际展示时添加了“全部”
    return (
      categoryList.findIndex(item => {
        return item?.id === cateId
      }) + 1
    )
  }

  return {
    props: {
      initialState,
      artworkDBType,
      workType,
      categoryList,
      productList: getGraphData(
        productRes?.data?.itemModule?.productList?.edges || []
      ),
      hasNextPage:
        productRes?.data?.itemModule?.productList?.pageInfo?.hasNextPage ||
        false,
      initParamSubCateIds: paramSubCateIds(),
      initCateIndex: initCateIndex(),
    },
  }
}

export default Market
